import React, { PureComponent } from "react";
import ReactDOM from "react-dom";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {
  CustomizedXAxisTickTime,
  CustomizedYAxisTick,
  selectFormat,
  Error,
  StandardTooltip,
  formatDateFromData,
  isDateFromDataInISOFormat,
} from "./../../../Utils/Utils.js";

const formatLabelMetric = (value) => selectFormat("number", value);

const COLORS = {
  youtube: "#FCD23A",
  facebook: "#16C8DE",
  twitter: "#3b5998",
  instagram: "#16DE85",
  date: "#333333",
};

const createLabel = (key, payload) => (
  <div className="standard-tooltip__item" key={key}>
    <span className="standard-tooltip__key">
      <span
        className="standard-tooltip__icon"
        style={{ background: COLORS[key] }}
      />
      {key}
    </span>
    <span className="standard-tooltip__value">
      {formatLabelMetric(payload[key])}
    </span>
  </div>
);

class VideoViewsByNetwork extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      networks: [],
      data: [],
    };
  }

  componentDidMount() {
    this.props.childRef(this);
  }

  toggleGraphValues = (property, data) => {
    let new_data = JSON.parse(JSON.stringify(data));

    new_data.forEach(function (entry, index) {
      Object.keys(entry).forEach(function () {
        property.forEach(function (name) {
          if (entry[name] !== null) {
            entry[name] = null;
          }
        });
      });
    });
    return new_data;
  };

  renderColorLegend = (value, entry) => {
    const { color } = entry;

    return (
      <span className="text-uppercase font-weight-bold" style={{ color }}>
        {value}
      </span>
    );
  };

  clickGraphLegend = (event) => {
    const value = event.value;
    let arr = this.state.networks;
    const data = formatDateFromData(this.props.data.data);

    // Legends
    const node = ReactDOM.findDOMNode(this.refs.social_graph);
    const list_elements = node.querySelectorAll(".recharts-legend-item");

    // Select clicked legend
    for (let i = 0; i < list_elements.length; i++) {
      if (list_elements[i].textContent === value) {
        list_elements[i]
          .querySelector(".recharts-symbols")
          .classList.toggle("recharts-symbols--off");
        list_elements[i]
          .querySelector(".recharts-legend-item-text")
          .classList.toggle("recharts-legend-item-text--off");
      }
    }

    // create an array of networks
    if (arr.indexOf(value) === -1) {
      arr.push(value);
    } else {
      arr.splice(arr.indexOf(value), 1);
    }

    // toggle network values
    let new_data = this.toggleGraphValues(arr, data);

    this.setState({ networks: arr, data: new_data });
  };

  renderLines = (networksArray) => {
    return networksArray.map((key) => {
      return (
        <Line
          key={key}
          type="monotone"
          dataKey={key}
          stroke={COLORS[key]}
          strokeWidth={2}
          dot={false}
        />
      );
    });
  };

  renderGraph = (data) => {
    // Remove Twitter
    // data.map((a,index) => {
    //   return delete a.twitter;
    // });

    let networksArray = [];

    data.forEach((item) => {
      Object.keys(item).forEach((item) => {
        if (!networksArray.includes(item) && item !== "date") {
          networksArray.push(item);
        }
      });
    });

    return (
      <ResponsiveContainer height={250}>
        <LineChart
          height={250}
          data={data}
          margin={{
            top: 20,
            right: 0,
            left: 5,
            bottom: 0,
          }}
          ref="social_graph"
        >
          <Tooltip
            cursor={{ fill: "#eee", opacity: 0.5 }}
            content={
              <StandardTooltip
                render={({ data }) => {
                  const payload = data(this.props);

                  if (payload !== null) {
                    let labels = [];

                    let tooltip_data = payload[0].payload;
                    let selected_date = payload[0].payload.date;

                    Object.keys(tooltip_data).forEach((key) => {
                      if (key === "date") {
                        return;
                      } else if (key === "youtube") {
                        return (labels[1] = createLabel(key, tooltip_data));
                      } else if (key === "facebook") {
                        return (labels[2] = createLabel(key, tooltip_data));
                      } else if (key === "twitter") {
                        return (labels[3] = createLabel(key, tooltip_data));
                      } else if (key === "instagram") {
                        return (labels[4] = createLabel(key, tooltip_data));
                      } else {
                      }
                    });

                    return (
                      <div className="standard-tooltip">
                        <header className="standard-tooltip__header">
                          <span className="standard-tooltip__key-header">
                            Date:
                          </span>
                          <span className="standard-tooltip__value-header">
                            {selected_date}
                          </span>
                        </header>
                        <main className="standard-tooltip__main">{labels}</main>
                      </div>
                    );
                  }
                }}
              />
            }
          />

          <CartesianGrid
            stroke="#FEE5DE"
            strokeDasharray="3 3"
            vertical={false}
          />

          <Legend
            verticalAlign="top"
            align="left"
            iconType="square"
            iconSize={15}
            height={65}
            formatter={this.renderColorLegend}
            onClick={this.clickGraphLegend}
          />
          {this.renderLines(networksArray)}
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            scale="point"
            tick={<CustomizedXAxisTickTime />}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            mirror={true}
            tick={<CustomizedYAxisTick />}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  getExportData = () => ({
    data: this.props.data.data,
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  render() {
    let graphData = this.props.data;

    if (this.state.networks.length) {
      graphData = this.state.data;
    } else {
      graphData = formatDateFromData(this.props.data.data);
    }

    if (graphData.hasData === false) {
      return <Error minHeight={360} />;
    }

    return this.renderGraph(graphData);
  }
}

export default VideoViewsByNetwork;
